import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

export const SMART_SLOT_LIMIT = 128;
const MINUTE = 60000;
const abort = () => Object.assign(new Error('Search cancelled'), { name: 'AbortError' });
const problem = (code) => Object.assign(new Error(code), { code });
const ids = (value) => Array.isArray(value) && value.length > 0
  && value.every((id) => Number.isInteger(id) && id > 0) && new Set(value).size === value.length;

export function spaLocalDateTime(value, timeZone) {
  return formatInTimeZone(value, timeZone, "yyyy-MM-dd'T'HH:mm");
}

export function smartSearchBounds(from, to, timeZone, interval, now = new Date()) {
  if (![15, 30].includes(Number(interval))) throw problem('invalidInterval');
  const parse = (text) => {
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(text)) throw problem('invalidRange');
    const instant = zonedTimeToUtc(text, timeZone);
    if (!Number.isFinite(instant.getTime()) || spaLocalDateTime(instant, timeZone) !== text) throw problem('invalidRange');
    return instant.getTime();
  };
  let start; let end;
  try { start = parse(from); end = parse(to); } catch (error) { throw problem(error.code || 'invalidRange'); }
  if (end < start || end - start > 7 * 24 * 60 * MINUTE) throw problem('invalidRange');
  start = Math.max(start, now.getTime());
  if (start > end) throw problem('pastRange');
  start = Math.ceil(start / MINUTE) * MINUTE;
  const minute = Number(formatInTimeZone(start, timeZone, 'mm'));
  start += (Number(interval) - minute % Number(interval)) % Number(interval) * MINUTE;
  return { start, end, step: Number(interval) * MINUTE };
}

function checkedCombination(result, requestedStart) {
  if (!result || typeof result.available !== 'boolean' || !Array.isArray(result.combinations)
    || !result.resourceRequirements || !Array.isArray(result.resourceRequirements.equipment)) throw problem('invalidResponse');
  // Without cabin requirements the existing engine does not validate or return cabins.
  if (!ids(result.resourceRequirements.compatibleCabinIds)) throw problem('configureCabins');
  if (!result.available) return null;
  if (!result.combinations.length || new Date(result.startAt).getTime() !== requestedStart
    || !(new Date(result.endAt).getTime() > requestedStart)) throw problem('invalidResponse');
  const requirements = result.resourceRequirements;
  const expectedOperators = Number(requirements.operatorCount || 1);
  const combo = result.combinations.find((item) => ids(item.operatorIds) && item.operatorIds.length === expectedOperators
    && ids(item.cabinIds) && item.cabinIds.every((id) => requirements.compatibleCabinIds.includes(id))
    && Array.isArray(item.equipment) && item.equipment.every((entry) => Number.isInteger(entry.equipmentId) && entry.equipmentId > 0
      && Number.isInteger(entry.quantity) && entry.quantity > 0)
    && new Set(item.equipment.map((entry) => entry.equipmentId)).size === item.equipment.length
    && requirements.equipment.every((required) => item.equipment.some((entry) => entry.equipmentId === required.equipmentId && entry.quantity >= required.quantity)));
  if (!combo) throw problem('invalidResponse');
  return combo;
}

export async function findFirstSpaSlot({ from, to, timeZone, interval, payload, check, signal, onProgress = () => {}, now = new Date(), limit = SMART_SLOT_LIMIT }) {
  const bounds = smartSearchBounds(from, to, timeZone, interval, now);
  const total = Math.floor((bounds.end - bounds.start) / bounds.step) + 1;
  let checked = 0;
  const assertActive = () => { if (signal?.aborted) throw abort(); };
  // Check in chronological order; business availability remains owned by the backend.
  for (let start = bounds.start; start <= bounds.end; start += bounds.step) {
    assertActive();
    if (checked >= limit) return { status: 'limited', checked, total };
    const result = await check({ ...payload, startAt: new Date(start).toISOString() }, { signal });
    assertActive();
    checked += 1;
    onProgress({ checked, total: Math.min(total, limit) });
    const combination = checkedCombination(result, start);
    if (combination) return { status: 'found', checked, total, result, combination };
  }
  return { status: 'empty', checked, total };
}
